import * as React from 'react';
import { Download, FileWarning } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface PdfPreviewCardProps {
  rowId: string;
}

export function PdfPreviewCard({ rowId }: PdfPreviewCardProps): JSX.Element {
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const fileUrl = `/api/v1/cockpit/queue/${encodeURIComponent(rowId)}/file`;

  React.useEffect(() => {
    setLoadError(null);
    let cancelled = false;
    void fetch(fileUrl, { method: 'HEAD', credentials: 'include' }).then((response) => {
      if (cancelled) return;
      if (!response.ok) {
        setLoadError(`Source PDF unavailable (HTTP ${response.status}).`);
      }
    }).catch((err: unknown) => {
      if (cancelled) return;
      setLoadError(err instanceof Error ? err.message : 'Unable to load source PDF.');
    });
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Source PDF</CardTitle>
          <CardDescription>
            Original document received from the customer. Use this view to confirm parsed fields.
          </CardDescription>
        </div>
        <Button asChild variant="outline" size="sm">
          <a href={fileUrl} download>
            <Download className="h-4 w-4" />
            Download
          </a>
        </Button>
      </CardHeader>
      <CardContent>
        {loadError ? (
          <div className="flex items-center gap-3 rounded-md border border-dashed border-amber-500/50 bg-amber-500/5 p-6 text-sm">
            <FileWarning className="h-5 w-5 text-amber-600" />
            <div>{loadError}</div>
          </div>
        ) : (
          <object
            data={fileUrl}
            type="application/pdf"
            className="h-[80vh] w-full rounded-md border bg-muted"
            aria-label="Source PDF preview"
          >
            <div className="flex flex-col items-start gap-2 p-6 text-sm">
              <div>This browser cannot inline-render PDFs.</div>
              <Button asChild variant="outline" size="sm">
                <a href={fileUrl} download>
                  <Download className="h-4 w-4" />
                  Download instead
                </a>
              </Button>
            </div>
          </object>
        )}
      </CardContent>
    </Card>
  );
}
